import type { CreditNoteResponse } from '@palaccount/contracts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount } from '@/components/amount';
import { ConfirmBox } from '@/components/confirm-box';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { localizeServerCause } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

import { computeApplyView, prefillOldestFirst } from './apply-credit';
import { useApplyCreditNote, useCustomerOpenInvoices } from './returns-api';

/**
 * Wave 4A — "Apply to invoices" on a posted sales return: the customer's
 * open invoices, oldest first, each with an amount cell PREFILLED by
 * BR-FIN-03-10's walk over the note's unapplied credit, in the product's one
 * shared box (GEN-204). Confirming sends the positive cells to the note's
 * `apply` route; the server re-reads every cap under lock and answers with
 * its own sentence when a cell is past one (the strip here only draws the
 * same tests early).
 *
 * The box is the sales editors' door; the RECEIPTS screen offers the same
 * act beside its cash grid (`receipt-editor-page.tsx`), where the open
 * invoices are already on screen.
 */
export function ApplyCreditNoteDialog({
  creditNote,
  open,
  onClose,
}: {
  creditNote: CreditNoteResponse;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const currency = creditNote.currency;
  const { data: invoices, isPending } = useCustomerOpenInvoices(open ? creditNote.partnerId : null);
  const rows = useMemo(
    () => (invoices ?? []).map((invoice) => ({ ...invoice, invoiceId: invoice.id })),
    [invoices],
  );
  const applyMutation = useApplyCreditNote(creditNote.id);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  // Prefill once per opening, when the list lands; the reader owns the
  // cells after that.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!open) {
      prefilled.current = false;
      setAmounts({});
      setError(null);
      return;
    }
    if (prefilled.current || invoices === undefined) return;
    prefilled.current = true;
    setAmounts(prefillOldestFirst(rows, creditNote.unappliedTotal, currency));
  }, [open, invoices, rows, creditNote.unappliedTotal, currency]);

  const view = useMemo(
    () => computeApplyView(amounts, rows, creditNote.unappliedTotal, currency),
    [amounts, rows, creditNote.unappliedTotal, currency],
  );

  const onConfirm = async () => {
    if (view.allocations.length === 0 || view.over) return;
    setError(null);
    try {
      await applyMutation.mutateAsync({ allocations: view.allocations });
      onClose();
    } catch (thrown) {
      setError(localizeServerCause(t, thrown, 'returns.errSaveFailed'));
    }
  };

  return (
    <ConfirmBox
      open={open}
      title={t('returns.applyDialogTitle', { code: creditNote.code })}
      confirmLabel={t('returns.applyConfirm')}
      onConfirm={() => void onConfirm()}
      onCancel={onClose}
      busy={applyMutation.isPending}
      body={
        <div className="grid gap-3">
          <p>{t('returns.applyDialogBody', { amount: creditNote.unappliedTotal })}</p>
          {isPending ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('returns.applyNoOpenInvoices')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('returns.colAppliedInvoice')}</TableHead>
                  <TableHead>{t('common.date')}</TableHead>
                  <TableHead className="text-end">{t('receipts.colBalanceDue')}</TableHead>
                  <TableHead className="w-36 text-end">{t('receipts.colApply')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const over = view.overRows.has(row.invoiceId);
                  return (
                    <TableRow key={row.invoiceId} className={cn(over && 'bg-red-50 dark:bg-red-950/40')}>
                      <TableCell className="font-mono">
                        <bdi dir="ltr">{row.code}</bdi>
                      </TableCell>
                      <TableCell>{row.invoiceDate}</TableCell>
                      <TableCell className="text-end">
                        <Amount value={row.balanceDue} currency={currency} />
                      </TableCell>
                      <TableCell>
                        <Input
                          inputMode="decimal"
                          dir="ltr"
                          aria-label={t('receipts.applyCreditAmount', { code: creditNote.code, invoice: row.code })}
                          aria-invalid={over || undefined}
                          className="text-end font-mono tabular-nums"
                          value={amounts[row.invoiceId] ?? ''}
                          onChange={(event) =>
                            setAmounts((current) => ({ ...current, [row.invoiceId]: event.target.value }))
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          <div
            role="status"
            className={cn(
              'flex flex-wrap items-center gap-4 rounded-md border px-3 py-2 text-sm',
              view.over
                ? 'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200'
                : 'border-border bg-muted/40',
            )}
          >
            <span className="me-auto">{view.over ? t('returns.applyOver') : t('returns.applyRemaining')}</span>
            <span className="font-mono text-xs">
              {t('receipts.totalApplied')} <Amount value={view.total} currency={currency} />
            </span>
            {view.over ? null : (
              <span className="font-mono text-xs">
                {t('returns.unappliedCredit')} <Amount value={view.remaining} currency={currency} />
              </span>
            )}
          </div>
          {error ? (
            <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </div>
      }
    />
  );
}

/**
 * The "Applied" table on a posted sales return — one row per application,
 * oldest first, the invoice clicking through — and the unapplied remainder
 * under it. Drawn by both return editors; absent while nothing is applied.
 */
export function CreditApplicationsCard({ creditNote }: { creditNote: CreditNoteResponse }) {
  const { t } = useTranslation();
  if (creditNote.applications.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('returns.appliedTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('returns.colAppliedInvoice')}</TableHead>
              <TableHead>{t('returns.colAppliedAt')}</TableHead>
              <TableHead className="text-end">{t('returns.colAppliedAmount')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {creditNote.applications.map((row, index) => (
              <TableRow key={`${row.invoiceId}:${index}`}>
                <TableCell>
                  <Link to={`/sales/${row.invoiceId}`} className="font-mono text-primary hover:underline">
                    <bdi dir="ltr">{row.invoiceCode}</bdi>
                  </Link>
                </TableCell>
                <TableCell>{row.appliedAt.slice(0, 10)}</TableCell>
                <TableCell className="text-end">
                  <Amount value={row.amount} currency={creditNote.currency} />
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={2} className="font-medium">
                {t('returns.unappliedCredit')}
              </TableCell>
              <TableCell className="text-end font-medium">
                <Amount value={creditNote.unappliedTotal} currency={creditNote.currency} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
